import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { Gift, Check, Spinner, Ticket } from "@phosphor-icons/react";

const COUNTRIES = [
  { code: "US", name: "United States" }, { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" }, { code: "NG", name: "Nigeria" },
  { code: "GH", name: "Ghana" }, { code: "KE", name: "Kenya" },
  { code: "CM", name: "Cameroon" }, { code: "FR", name: "France" },
  { code: "IN", name: "India" }, { code: "ZA", name: "South Africa" },
];

export default function GiftCards() {
  const qc = useQueryClient();
  const [country, setCountry] = useState("US");
  const [categoryId, setCategoryId] = useState("");
  const [selected, setSelected] = useState(null);
  const [denom, setDenom] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [rangeAmount, setRangeAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [senderName, setSenderName] = useState("");
  const [phone, setPhone] = useState("");
  const [qty, setQty] = useState(1);

  const { data: wallet } = useQuery({ queryKey: ["wallet"], queryFn: async () => (await api.get("/wallet")).data });
  const { data: status } = useQuery({ queryKey: ["gc-status"], queryFn: async () => (await api.get("/giftcards/status")).data });
  const { data: cats } = useQuery({
    queryKey: ["gc-cats"],
    queryFn: async () => (await api.get("/giftcards/categories")).data,
    enabled: !!status?.configured,
  });
  const { data: countryData } = useQuery({
    queryKey: ["gc-countries"],
    queryFn: async () => (await api.get("/giftcards/countries")).data,
    enabled: !!status?.configured,
  });
  const countries = countryData?.countries?.length ? countryData.countries : COUNTRIES;
  const { data: products, isFetching, isError } = useQuery({
    queryKey: ["gc-products", country, categoryId],
    queryFn: async () => (await api.get(`/giftcards/products?country=${country}${categoryId ? `&category_id=${categoryId}` : ""}`)).data,
    enabled: !!status?.configured,
  });
  const { data: orders } = useQuery({
    queryKey: ["gc-orders"],
    queryFn: async () => (await api.get("/giftcards/orders")).data,
    refetchInterval: 4000,
  });

  const isRange = selected && (selected.denomination_type === "RANGE" || (selected.range && (selected.denominations || []).length === 0));
  const priceCents = isRange ? quote?.price_cents : denom?.price_cents;

  useEffect(() => {
    if (!isRange || !rangeAmount || parseFloat(rangeAmount) <= 0) { setQuote(null); return; }
    setQuoting(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/giftcards/quote?product_id=${selected.product_id}&amount=${parseFloat(rangeAmount)}`);
        setQuote(data);
      } catch { setQuote(null); }
      finally { setQuoting(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [rangeAmount, isRange, selected]);

  const buy = async () => {
    const recipientDenom = isRange ? parseFloat(rangeAmount) : denom?.recipient_denom;
    if (!selected || !recipientDenom || !email) { toast.error("Pick a card, amount and recipient email."); return; }
    setBusy(true);
    try {
      await api.post("/giftcards/order", {
        product_id: selected.product_id, recipient_denom: recipientDenom,
        recipient_email: email, quantity: qty, country,
        sender_name: senderName, recipient_phone: phone, recipient_phone_country: country,
      });
      toast.success("Gift card purchased — we're delivering it to the recipient now.");
      setSelected(null); setDenom(null); setEmail(""); setRangeAmount(""); setQuote(null);
      setSenderName(""); setPhone(""); setQty(1);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["ledger"] });
      qc.invalidateQueries({ queryKey: ["gc-orders"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (status && !status.configured) {
    return (
      <div className="space-y-6" data-testid="giftcards-page">
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Gift Cards</h1>
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground" data-testid="gc-not-configured">
          Gift cards are being set up. Please check back shortly.
        </div>
      </div>
    );
  }

  const list = products?.products || [];

  return (
    <div className="space-y-8" data-testid="giftcards-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Gift Cards</h1>
          <p className="mt-2 text-muted-foreground">Buy a gift card for any occasion — paid from your wallet, delivered by email.</p>
        </div>
        <div className="rounded-2xl border border-border bg-card px-4 py-2 text-sm">
          Wallet: <span data-testid="gc-wallet-balance" className="font-mono-metric font-semibold">{wallet ? money(wallet.balance_cents, wallet.currency) : "—"}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <select data-testid="gc-country" value={country} onChange={(e) => { setCountry(e.target.value); setSelected(null); setDenom(null); }}
          className="rounded-xl border border-input bg-background px-4 py-2.5 text-sm">
          {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        <select data-testid="gc-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
          className="rounded-xl border border-input bg-background px-4 py-2.5 text-sm">
          <option value="">All categories</option>
          {(cats?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {isFetching && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="animate-spin" /> Loading gift cards…</div>}
      {isError && <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">No gift cards available for this country right now.</div>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((p) => (
          <button key={p.product_id} data-testid={`gc-product-${p.product_id}`} onClick={() => { setSelected(p); setDenom(null); setRangeAmount(""); setQuote(null); setSenderName(""); setPhone(""); setQty(1); }}
            className={`flex flex-col gap-3 rounded-2xl border p-4 text-left transition-colors ${selected?.product_id === p.product_id ? "border-primary bg-accent" : "border-border bg-card hover:border-primary/50"}`}>
            <div className="flex items-center gap-3">
              {p.logo ? <img src={p.logo} alt={p.name} className="h-10 w-10 rounded-lg object-contain" />
                : <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-primary"><Gift size={20} weight="fill" /></div>}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{p.name}</p>
                <p className="truncate text-xs text-muted-foreground">{p.brand || p.country}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{p.denominations.length} amount{p.denominations.length === 1 ? "" : "s"} available</p>
          </button>
        ))}
      </div>

      {selected && (
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="gc-purchase-panel">
          <h2 className="font-display text-lg font-semibold">{selected.name}</h2>
          <p className="mt-1 text-xs text-muted-foreground">Choose an amount (charged from your wallet in USD).</p>
          {isRange ? (
            <div className="mt-4 max-w-xs" data-testid="gc-range">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Amount in {selected.recipient_currency} · {selected.range?.min?.toLocaleString()}–{selected.range?.max?.toLocaleString()}
              </label>
              <input data-testid="gc-range-amount" type="number" min={selected.range?.min} max={selected.range?.max}
                value={rangeAmount} onChange={(e) => setRangeAmount(e.target.value)}
                placeholder={`e.g. ${selected.range?.min}`}
                className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none" />
              <p className="mt-2 text-xs text-muted-foreground" data-testid="gc-range-price">
                {quoting ? "Pricing…" : quote
                  ? <>You pay <span className="font-semibold text-foreground">{money(quote.price_cents)}</span> from your wallet</>
                  : `Enter an amount between ${selected.range?.min?.toLocaleString()} and ${selected.range?.max?.toLocaleString()} ${selected.recipient_currency}`}
              </p>
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              {selected.denominations.map((d) => (
                <button key={d.recipient_denom} data-testid={`gc-denom-${d.recipient_denom}`} onClick={() => setDenom(d)}
                  className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${denom?.recipient_denom === d.recipient_denom ? "border-primary bg-accent text-accent-foreground" : "border-border"}`}>
                  {d.recipient_denom} {d.recipient_currency}
                  <span className="ml-2 text-xs text-muted-foreground">{money(d.price_cents)}</span>
                </button>
              ))}
            </div>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Recipient email</label>
              <input data-testid="gc-recipient-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Recipient phone <span className="normal-case opacity-60">(optional)</span></label>
              <input data-testid="gc-recipient-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 202 555 0123"
                className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">From <span className="normal-case opacity-60">(sender name)</span></label>
              <input data-testid="gc-sender-name" value={senderName} onChange={(e) => setSenderName(e.target.value)}
                placeholder="Your name"
                className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Quantity</label>
              <input data-testid="gc-quantity" type="number" min="1" max="10" value={qty}
                onChange={(e) => setQty(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none" />
            </div>
          </div>
          <button data-testid="gc-buy" onClick={buy} disabled={busy || !priceCents || !email}
            className="mt-5 flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
            {busy ? <><Spinner className="animate-spin" /> Processing…</> : <>Buy {priceCents ? money(priceCents * qty) : ""} from wallet</>}
          </button>
        </div>
      )}

      <div data-testid="gc-orders">
        <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          <Ticket size={14} /> Your gift cards
        </h2>
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          {(orders?.orders || []).length === 0 && <p className="px-4 py-10 text-center text-sm text-muted-foreground">No gift cards yet</p>}
          {(orders?.orders || []).map((o) => (
            <div key={o.id} data-testid={`gc-order-${o.id}`} className="border-b border-border px-4 py-3 last:border-0">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{o.product_name} · {o.recipient_denom} {o.recipient_currency}</p>
                  <p className="truncate text-xs text-muted-foreground">{o.recipient_email} · {shortDate(o.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono-metric text-sm font-semibold">{money(o.price_cents)}</p>
                  <span className={`text-xs font-semibold ${o.status === "delivered" ? "text-emerald-600" : o.status === "failed" ? "text-red-600" : "text-amber-600"}`}>
                    {o.status === "delivered" ? <span className="inline-flex items-center gap-1"><Check size={12} weight="bold" /> delivered</span> : o.status}
                  </span>
                </div>
              </div>
              {o.status === "delivered" && (o.cards || []).length > 0 && (
                <div className="mt-2 space-y-1 rounded-xl bg-background p-3 text-xs" data-testid={`gc-codes-${o.id}`}>
                  {o.cards.map((c, i) => (
                    <p key={i} className="font-mono-metric">
                      {c.cardNumber && <>Card: <b>{c.cardNumber}</b> </>}
                      {c.pinCode && <>· PIN: <b>{c.pinCode}</b> </>}
                      {c.redemptionUrl && <>· <a className="text-primary underline" href={c.redemptionUrl} target="_blank" rel="noreferrer">Redeem</a></>}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
